import { jsPDF } from 'jspdf';
import { formatLevelDisplay } from './levelUtils';

/**
 * Generates a PDF advocacy report from the structured data returned by exportUserData.
 * Uses jsPDF with the built-in Helvetica font (no external font needed).
 * Returns the jsPDF document instance — caller can call doc.save() to download.
 */
export const generateExportPDF = (userData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = 20;

  const checkPageBreak = (neededHeight = 20) => {
    if (y + neededHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // ── Title ────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(0, 0, 51); // deep navy
  doc.text('My Advocacy Journey', margin, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  if (userData.user.displayName) {
    doc.text(userData.user.displayName, margin, y);
    y += 6;
  }
  doc.text(`Exported: ${formatDate(userData.exportDate)}`, margin, y);
  y += 14;
  doc.setTextColor(0, 0, 0);

  // ── My Causes ────────────────────────────────────────────────────────────
  if (userData.causes && userData.causes.length > 0) {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 51);
    doc.text('My Causes', margin, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    for (const cause of userData.causes) {
      checkPageBreak(8);
      doc.text(`${cause.title}  |  ${formatLevelDisplay(cause.level)}`, margin + 4, y);
      y += 7;
    }
    y += 8;
  }

  // ── Activities (grouped by cause) ────────────────────────────────────────
  if (userData.activities && userData.activities.length > 0) {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 51);
    doc.text('Activities', margin, y);
    y += 8;

    // Group activities by cause
    const byCause = {};
    for (const act of userData.activities) {
      const key = act.cause || 'Uncategorized';
      if (!byCause[key]) byCause[key] = [];
      byCause[key].push(act);
    }

    for (const [causeName, acts] of Object.entries(byCause)) {
      checkPageBreak(14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(causeName, margin + 4, y);
      y += 7;

      for (const act of acts) {
        checkPageBreak(28);

        // Activity title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        const titleLines = doc.splitTextToSize(act.title, contentWidth - 8);
        for (const line of titleLines) {
          checkPageBreak(6);
          doc.text(line, margin + 8, y);
          y += 5;
        }

        // Level + date
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        const meta = [formatLevelDisplay(act.level), act.date ? formatDate(act.date) : '']
          .filter(Boolean)
          .join('  |  ');
        if (meta) {
          checkPageBreak(6);
          doc.text(meta, margin + 8, y);
          y += 5;
        }
        doc.setTextColor(0, 0, 0);

        // Reflection
        if (act.reflection) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(10);
          doc.setTextColor(60, 60, 60);
          const reflLines = doc.splitTextToSize(`Reflection: ${act.reflection}`, contentWidth - 8);
          for (const line of reflLines) {
            checkPageBreak(6);
            doc.text(line, margin + 8, y);
            y += 5;
          }
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
        }

        // Photo note
        if (act.hasPhoto) {
          doc.setFontSize(10);
          doc.setTextColor(128, 128, 128);
          checkPageBreak(6);
          doc.text('(Photo attached)', margin + 8, y);
          doc.setTextColor(0, 0, 0);
          y += 5;
        }

        y += 4;
      }
      y += 4;
    }
    y += 4;
  }

  // ── Milestones ───────────────────────────────────────────────────────────
  if (userData.milestones && userData.milestones.length > 0) {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 51);
    doc.text('Milestones', margin, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    for (const m of userData.milestones) {
      checkPageBreak(8);
      const dateStr = m.date ? formatDate(m.date) : '';
      doc.text(`${m.name}${dateStr ? '  —  ' + dateStr : ''}`, margin + 4, y);
      y += 7;
    }
    y += 8;
  }

  // ── Level Elevations ──────────────────────────────────────────────────────
  if (userData.elevations && userData.elevations.length > 0) {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 51);
    doc.text('Level Elevations', margin, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    for (const e of userData.elevations) {
      checkPageBreak(8);
      const dateStr = e.date ? formatDate(e.date) : '';
      doc.text(
        `${e.cause}: ${e.from} to ${e.to}${dateStr ? '  —  ' + dateStr : ''}`,
        margin + 4,
        y
      );
      y += 7;
    }
  }

  return doc;
};

/**
 * Generate a CSV string from exported user data (activities only).
 * Columns: Date, Cause, Level, Activity, Reflection, Has Photo
 */
export const generateExportCSV = (exportData) => {
  const BOM = '\uFEFF';
  const headers = ['Date', 'Cause', 'Level', 'Activity', 'Reflection', 'Has Photo'];

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const rows = (exportData.activities || []).map(activity => {
    const date = activity.timestamp
      ? new Date(activity.timestamp).toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      : '';
    const cause = activity.cause || '';
    const level = activity.level || '';
    const title = activity.title || '';
    const reflection = activity.reflection || '';
    const hasPhoto = activity.hasPhoto ? 'Yes' : 'No';

    return [date, cause, level, title, reflection, hasPhoto].map(escapeCSV).join(',');
  });

  return BOM + [headers.join(','), ...rows].join('\r\n');
};
